/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select, DatePicker, Space, Typography, Alert, Tag } from 'antd';
import { css } from '@emotion/react';
import commonStyles from '../../styles/commonStyles';
import dayjs from 'dayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetEventTemplatesQuery, useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { useCreateEventFromTemplateMutation } from '../../api/plantingEventsApi';
import { useGetSaveUsersQuery } from '../../api/saveUsersApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { EventTemplateCategory } from '../../types/eventTemplates';
import { useSave } from '../../contexts/WorldContext';

const { TextArea } = Input;
const { Text } = Typography;

const styles = {
  iconLarge: css({ fontSize: '20px' }),
  customTag: css({ marginLeft: 4 }),
  listItem: css({ marginBottom: 0, paddingLeft: 20 }),
  formSection: css({ marginTop: 16 }),
  infoAlert: css({ marginTop: 8 }),
};

interface CreateBatchEventModalProps {
  farmId: string;
  plantingIds: string[]; // Массив UUID посадок
  bedIds?: number[]; // Опционально - массив ID грядок
  plotIds?: number[]; // Опционально - массив ID участков
  fieldIds?: number[]; // Опционально - массив ID полей
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Модальное окно для создания BATCH (групповых) событий из шаблона (CN-0008)
 * 
 * Особенности:
 * - Принимает множественные цели (plantingIds/bedIds/plotIds/fieldIds)
 * - Автоматически создает parent + children события на бэкенде
 * - Parent событие отображается в очереди с прогрессом
 * - Children события выполняются индивидуально
 * 
 * Workflow:
 * 1. Пользователь выбирает несколько целей (чекбоксы в таблице)
 * 2. Кликает "Массовые мероприятия"
 * 3. Выбирает шаблон и назначает исполнителя
 * 4. Бэкенд создает 1 parent + N children событий
 * 5. В очереди показывается только parent с прогрессом
 */
const CreateBatchEventModal: React.FC<CreateBatchEventModalProps> = ({
  farmId,
  plantingIds = [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // saveId resolves through useSave() (Phase 4 — save-scope events).
  bedIds = [],
  plotIds = [],
  fieldIds = [],
  open,
  onClose,
  onSuccess,
}) => {
  const save = useSave();
  const [form] = Form.useForm();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Вычисляем общее количество целей
  const totalTargets = plantingIds.length + bedIds.length + plotIds.length + fieldIds.length;

  // Загружаем шаблоны событий
  const {
    data: templatesData,
    isLoading: loadingTemplates,
  } = useGetEventTemplatesQuery(
    { saveId: save?.id || '', isActive: true },
    { skip: !open || !save?.id }
  );
  const templates = templatesData?.templates ?? [];

  // Загружаем детали выбранного шаблона
  const {
    data: templateDetail,
  } = useGetEventTemplateByIdQuery(
    selectedTemplateId && open && save?.id ? { id: selectedTemplateId, saveId: save.id } : skipToken
  );

  // Загружаем пользователей сейва для назначения
  const {
    data: farmUsers = [],
    isLoading: loadingUsers,
  } = useGetSaveUsersQuery(save.id, { 
    skip: !open || !selectedTemplateId || !templateDetail?.assignable 
  });

  const [createBatchEvent, { isLoading: creating }] = useCreateEventFromTemplateMutation();

  // Сбросить форму при открытии
  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedTemplateId(null);
    }
  }, [open, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!selectedTemplateId) {
        toastError('Выберите шаблон мероприятия');
        return;
      }

      if (totalTargets === 0) {
        toastError('Нет выбранных целей для создания событий');
        return;
      }

      // Формируем массив targets из всех типов целей
      const targets: Array<{
        plantingId?: string;
        bedId?: string;
        plotId?: string;
        fieldId?: string;
      }> = [
        ...plantingIds.map(id => ({ plantingId: id })),
        ...bedIds.map(id => ({ bedId: String(id) })),
        ...plotIds.map(id => ({ plotId: String(id) })),
        ...fieldIds.map(id => ({ fieldId: String(id) })),
      ];

      // Формируем payload для создания batch события
      const payload = {
        templateId: selectedTemplateId,
        farmId,
        targets,
        plannedDate: values.plannedDate ? dayjs(values.plannedDate).format('YYYY-MM-DD') : undefined,
        notes: values.notes || undefined,
        assignedTo: values.assignedTo || undefined,
      };

      // Используем useSave context для resolve'а saveId. Phase 4: events живут в save-scope.
      const result = await createBatchEvent({ saveId: save.id, payload }).unwrap();
      
      const eventCount = result.events.length;
      const isBatch = eventCount > 1;
      
      if (isBatch) {
        toastSuccess(
          `Создано ${eventCount} событий для массового мероприятия`
        );
      } else {
        toastSuccess(`Создано событие`);
      }
      
      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Error creating batch event:', error);
      toastError(error?.data?.message || error?.message || 'Ошибка при создании событий');
    }
  };

  // Группировка шаблонов по категориям
  const templatesByCategory = templates.reduce((acc, template) => {
    const category = template.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<EventTemplateCategory, typeof templates>);

  const categoryNames: Record<EventTemplateCategory, string> = {
    inspection: 'Осмотры',
    measurement: 'Измерения',
    treatment: 'Обработки',
    care: 'Уход',
    harvest: 'Урожай',
    maintenance: 'Обслуживание',
    lifecycle: 'Жизненный цикл',
  };

  return (
    <Modal
      title="Создать массовое событие"
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Создать"
      cancelText="Отмена"
      confirmLoading={creating}
      width={650}
    >
      <Space direction="vertical" size="middle" css={commonStyles.fullWidth}>
        <Alert
          type="info"
          message={`Выбрано целей: ${totalTargets}`}
          description={
            <div>
              {plantingIds.length > 0 && <div>• Посадки: {plantingIds.length}</div>}
              {bedIds.length > 0 && <div>• Грядки: {bedIds.length}</div>}
              {plotIds.length > 0 && <div>• Участки: {plotIds.length}</div>}
              {fieldIds.length > 0 && <div>• Поля: {fieldIds.length}</div>}
              <div css={styles.infoAlert}>
                {totalTargets > 1
                  ? 'Будет создано 1 массовое событие-контейнер и индивидуальные задачи для каждой цели.'
                  : 'Будет создано 1 обычное событие.'}
              </div>
            </div>
          }
          showIcon
        />

        {selectedTemplateId && templateDetail && (
          <Alert
            type="success"
            message={
              <Space>
                {templateDetail.icon && <span css={styles.iconLarge}>{templateDetail.icon}</span>}
                <Text strong>{templateDetail.name}</Text>
                {templateDetail.scopeType === 'farm' && (
                  <Tag color="purple">Кастомный</Tag>
                )}
              </Space>
            }
            description={templateDetail.description}
            showIcon={false}
          />
        )}
      </Space>

      <Form form={form} layout="vertical" css={styles.formSection}>
        {/* Выбор шаблона - ОБЯЗАТЕЛЬНО */}
        <Form.Item
          label="Шаблон мероприятия"
          name="templateId"
          rules={[{ required: true, message: 'Выберите шаблон' }]}
          help="Выберите тип задачи. Для каждой цели будет создана индивидуальная задача."
        >
          <Select
            placeholder="Выберите шаблон"
            loading={loadingTemplates}
            value={selectedTemplateId}
            onChange={(val) => {
              setSelectedTemplateId(val);
              form.setFieldValue('assignedTo', undefined); // Сбросить исполнителя при смене шаблона
            }}
            showSearch
            optionFilterProp="children"
          >
            {Object.entries(categoryNames).map(([category, categoryName]) => {
              const categoryTemplates = templatesByCategory[category as EventTemplateCategory];
              if (!categoryTemplates || categoryTemplates.length === 0) return null;

              return (
                <Select.OptGroup key={category} label={categoryName}>
                  {categoryTemplates.map((template) => (
                    <Select.Option key={template.id} value={template.id}>
                      <Space>
                        {template.icon && <span>{template.icon}</span>}
                        <span>{template.name}</span>
                        {template.scopeType === 'farm' && (
                          <Tag color="purple" css={styles.customTag}>custom</Tag>
                        )}
                      </Space>
                    </Select.Option>
                  ))}
                </Select.OptGroup>
              );
            })}
          </Select>
        </Form.Item>

        {/* Назначение исполнителя (только если assignable) */}
        {selectedTemplateId && templateDetail?.assignable && (
          <Form.Item
            label="Исполнитель"
            name="assignedTo"
            help="Назначить выполнение всех задач на конкретного сотрудника. Можно оставить пустым."
          >
            <Select
              placeholder="Выберите исполнителя (опционально)"
              allowClear
              loading={loadingUsers}
              showSearch
              optionFilterProp="children"
            >
              {farmUsers.map((farmUser) => (
                <Select.Option key={farmUser.user.id} value={farmUser.user.id}>
                  <Space>
                    <Text>{farmUser.user.username || farmUser.user.email || farmUser.user.id}</Text>
                    <Tag color="blue">{farmUser.membership.role.code}</Tag>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {/* Дата планирования */}
        <Form.Item
          label="Плановая дата выполнения"
          name="plannedDate"
          help="Когда планируется выполнить задачи (опционально)"
        >
          <DatePicker
            css={commonStyles.fullWidth}
            placeholder="Выберите дату"
            format="DD.MM.YYYY"
          />
        </Form.Item>

        {/* Примечания */}
        <Form.Item
          label="Примечания"
          name="notes"
        >
          <TextArea
            rows={3}
            placeholder="Общие инструкции для всех задач (например: 'Приоритетный осмотр', 'Измерить только крупные экземпляры')"
          />
        </Form.Item>

        {/* Информация об атрибутах шаблона */}
        {templateDetail && templateDetail.properties && templateDetail.properties.length > 0 && (
          <Alert
            type="info"
            message="Параметры для заполнения при выполнении"
            description={
              <ul css={styles.listItem}>
                {templateDetail.properties.map((attr) => (
                  <li key={attr.propertyId}>
                    <Text>{attr.name}</Text>
                    {attr.isRequired && <Text type="danger"> *</Text>}
                    {attr.unit && <Text type="secondary"> ({attr.unit})</Text>}
                  </li>
                ))}
              </ul>
            }
          />
        )}
      </Form>
    </Modal>
  );
};

export default CreateBatchEventModal;
